import './check-item-clarification-reply.scss'
import {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  FileUpload,
} from '@vedmarket/ui-kit'
import {
  APPLICATION_DOCUMENT_ACCEPTED_FILES,
  COLORS,
} from 'config/constants'

const CheckItemClarificationReply = ({
  clarification,
  runAction,
  onAnswer,
}: ApplicationCheckItemClarificationReplyProps) => {
  const { t } = useTranslation()
  const [ answerText, setAnswerText ] = useState('')
  const [ pendingFiles, setPendingFiles ] = useState<File[]>([])

  const canSend = Boolean(answerText.trim()) || pendingFiles.length > 0

  const handleFiles = (files: File[]) => {
    setPendingFiles((current) => [ ...current, ...files ])
    return false
  }

  const removePendingFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleSend = () => {
    if (!canSend) return
    runAction(() => onAnswer({
      clarificationId: clarification.id,
      answer_text: answerText.trim(),
      attachments: pendingFiles,
    }).then(() => {
      setAnswerText('')
      setPendingFiles([])
    }), 'applications__message__clarificationAnswered')
  }

  return (
    <Box className='application-check-item-clarification-reply'>
      <Stack spacing={1}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {clarification.title || t('applications__text__clarification')}
        </Typography>
        <Typography variant='body2'>{clarification.question}</Typography>
      </Stack>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label={<Typography component='span' variant='inherit'>{t('applications__text__clarificationAnswer')}</Typography>}
        placeholder={t('applications__text__clarificationAnswerPlaceholder')}
        value={answerText}
        onChange={(e) => setAnswerText(e.target.value)}
      />

      {pendingFiles.length > 0 && (
        <>
          <Alert severity='info' sx={{
            paddingTop: 0.5,
            paddingBottom: 0.5,
          }}>
            <Typography variant='body2'>
              {t('applications__text__clarificationPendingFilesHint')}
            </Typography>
          </Alert>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
            {pendingFiles.map((file, index) => (
              <FileUpload
                key={`${file.name}-${file.lastModified}-${index}`}
                acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
                initialFileTitle={file.name}
                onRemove={() => removePendingFile(index)}
              />
            ))}
          </Box>
        </>
      )}

      <FileUpload
        multiple
        acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
        onHandleFiles={handleFiles}
      />

      <Stack spacing={1}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant='contained'
            disabled={!canSend}
            onClick={handleSend}
          >
            <Typography component='span' variant='inherit'>
              {pendingFiles.length > 0
                ? t('applications__text__sendAnswerWithFiles')
                : t('applications__text__sendAnswer')}
            </Typography>
          </Button>
        </Box>
        <Typography variant='caption' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY, display: 'block' }}>
          {t('applications__text__clarificationAttachHint')}
        </Typography>
      </Stack>
    </Box>
  )
}

export default CheckItemClarificationReply
